import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import Accordion from '@repo/ui/accordion'
import Box from '@repo/ui/box'
import { ExclamationMarkOutlinedIcon, LocationOneIcon, TravelTwoIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { getEnrollmentDispatchPlaces } from '../../../api/enrollments/get-enrollment-dispatch-places'
import { ClientHeader } from '../../../components/pages/language-test/client-space/shared-client-space-ui'
import { DispatchPlaceMeetingPlaceSection } from '../../../components/pages/language-test/client-space/shared-client-space-ui/meeting-place-section'
import { TripStepCard } from '../../../components/pages/language-test/client-space/shared-client-space-ui/trip-step-card'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import {
  enrollmentDispatchPlacesFields,
  EnrollmentDispatchPlacesType
} from '../../../lib/types/fields/dispatch-places/dispatch-place'
import { getTravelScheduleTripTypeOption } from '../../../lib/utils/functions/travel-schedule.functions'
import { withFrenchDe } from '../../../lib/utils/functions/user.functions'
function sortDispatchPlaces<
  T extends {
    tripStep?: {
      departureDate?: string
      arrivalDate?: string
      departureTime?: { hours: number; minutes: number }
      arrivalTime?: { hours: number; minutes: number }
    }
  }
>(dispatchPlaces: T[], directionality: TripStepTypeEnum): T[] {
  return dispatchPlaces.sort((a, b) => {
    const isInbound = directionality === TripStepTypeEnum.INBOUND

    const aTime = isInbound ? a.tripStep?.departureTime : a.tripStep?.arrivalTime
    const bTime = isInbound ? b.tripStep?.departureTime : b.tripStep?.arrivalTime
    const aDateStr = isInbound ? a.tripStep?.departureDate : a.tripStep?.arrivalDate
    const bDateStr = isInbound ? b.tripStep?.departureDate : b.tripStep?.arrivalDate

    const aDate = aTime ? DateFormatter.createDate(aTime, aDateStr) : null
    const bDate = bTime ? DateFormatter.createDate(bTime, bDateStr) : null

    if (aDate && bDate) return aDate.getTime() - bDate.getTime()

    return 0
  })
}

function getSortedDispatchPlaces<
  T extends {
    directionality: TripStepTypeEnum
    tripStep?: {
      departureDate?: string
      arrivalDate?: string
      departureTime?: { hours: number; minutes: number }
      arrivalTime?: { hours: number; minutes: number }
    }
  }
>(
  dispatchPlaces: T[]
): {
  allerDispatchPlaces: T[]
  retourDispatchPlaces: T[]
} {
  const allerDispatchPlaces = dispatchPlaces.filter(
    (dp) => dp.directionality === TripStepTypeEnum.INBOUND
  )
  const retourDispatchPlaces = dispatchPlaces.filter(
    (dp) => dp.directionality === TripStepTypeEnum.OUTBOUND
  )
  const sortedAllerDispatchPlaces = sortDispatchPlaces(
    allerDispatchPlaces,
    TripStepTypeEnum.INBOUND
  )
  const sortedRetourDispatchPlaces = sortDispatchPlaces(
    retourDispatchPlaces,
    TripStepTypeEnum.OUTBOUND
  )
  return {
    allerDispatchPlaces: sortedAllerDispatchPlaces,
    retourDispatchPlaces: sortedRetourDispatchPlaces
  }
}

export default function ClientTripsPage() {
  const { enrollment } = useEnrollment()
  const [accordionOpen, setAccordionOpen] = useState<Record<number, boolean>>({})

  const { data: dispatchPlaces } = useQuery({
    queryKey: [
      'enrollment-dispatch-places',
      enrollment.id,
      'version-partial',
      'sort-directionality',
      'sort-departure-time'
    ],
    queryFn: async () => {
      const res = (await getEnrollmentDispatchPlaces(
        enrollment.id,
        enrollmentDispatchPlacesFields
      )) as EnrollmentDispatchPlacesType[]

      // open all accordions by default
      const accordionState = res.reduce((acc, dp) => ({ ...acc, [dp.id]: true }), {})
      setAccordionOpen(accordionState)

      return res
    },
    enabled: !!enrollment.id
  })

  // get all dispatch places that are not take in charge
  const displayDispatchPlaces = useMemo(() => {
    const allDispatchPlaces: EnrollmentDispatchPlacesType[] = []
    const removedIsTakenCharge = dispatchPlaces?.filter((dp) => !dp.tripBooking?.isTakeInCharge)

    const { allerDispatchPlaces, retourDispatchPlaces } = getSortedDispatchPlaces(
      removedIsTakenCharge ?? []
    )

    // The trips displayed should be the first departing trip for the aller journey (which will always be the secondary reservation) if we don't have one, add the first aller dispatch place
    if (allerDispatchPlaces.length) {
      // check if we have a secondary reservation
      const firstAllerDispatchPlaces = removedIsTakenCharge?.find(
        (dp) => dp.tripBooking?.mainReservationId
      )

      const secondaryAllerDispatchPlaces = allerDispatchPlaces.find(
        (dp) => dp.type === TripTypeEnum.SECONDARY
      )

      // if we have one, add it to the allDispatchPlaces else add the first aller dispatch place
      if (firstAllerDispatchPlaces) allDispatchPlaces.push(firstAllerDispatchPlaces)
      else if (secondaryAllerDispatchPlaces) allDispatchPlaces.push(secondaryAllerDispatchPlaces)
      else allDispatchPlaces.push(allerDispatchPlaces[0])
    }

    // the last departing trip for the retour journey.
    if (retourDispatchPlaces.length) {
      const last = retourDispatchPlaces.pop()
      if (last) allDispatchPlaces.push(last)
    }

    return allDispatchPlaces
  }, [dispatchPlaces])

  // the departure (INBOUND) block reads "Aller" when a return (OUTBOUND) block is shown next to it,
  // "Aller Simple" otherwise
  const hasReturnTrip = displayDispatchPlaces.some(
    (dp) => dp.directionality === TripStepTypeEnum.OUTBOUND
  )

  // the backend only returns the places once LEC has published them (dispatched on a locked booking)
  const isNotPublished = dispatchPlaces?.length === 0

  return (
    <div className={'flex flex-col gap-4'}>
      <ClientHeader title={'RDV trajet aller/retour'} icon={TravelTwoIcon} />
      {isNotPublished ? (
        <Box className={'bg-white-hover border border-grey-light rounded-lg p-4'}>
          <Typography variant={'secondary-body'}>
            Les informations (lieu et horaires) concernant le RDV pour le trajet de votre enfant
            seront disponibles prochainement (en général, environ 12 jours avant le départ). Vous
            recevrez un e-mail dès que celles-ci seront disponibles. En attendant, aucune
            inquiétude : c’est son encadrant qui sera en possession de son billet et qui le lui
            remettra sur place le jour du départ !
          </Typography>
        </Box>
      ) : (
        <Box
          className={'flex flex-col gap-2 bg-white-hover border border-grey-light rounded-lg p-4'}
        >
          <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
            Voici les horaires de voyage{' '}
            {withFrenchDe(
              enrollment.childFirstName
                ? StringFormatter.capitalize(enrollment.childFirstName)
                : '-'
            )}
            .
          </Typography>

          <div className={'flex flex-row gap-2 items-center'}>
            <ExclamationMarkOutlinedIcon
              color={tailwindColors.red.DEFAULT}
              className={'w-10 h-10 min-w-10 min-h-10'}
            />

            <Typography variant={'secondary-body'}>
              Attention : il ne pourra pas être pris en charge sans l'ensemble des documents
              demandés sur la page "Check-list des documents à présenter au départ" Le jour du
              départ, rendez-vous directement au point de rassemblement : un responsable LEC
              (blouson rouge) vous y accueillera.
            </Typography>
          </div>
        </Box>
      )}

      <div className={'grid grid-cols-2 gap-4 mobile:grid-cols-1 tab:grid-cols-1'}>
        {displayDispatchPlaces?.map((dispatchPlace) => (
          <TripStepCard
            key={dispatchPlace.id}
            dispatchPlace={dispatchPlace}
            hasReturnTrip={hasReturnTrip}
          />
        ))}
      </div>

      {displayDispatchPlaces?.map((dispatchPlace) => {
        const { label } = getTravelScheduleTripTypeOption(
          dispatchPlace.directionality,
          hasReturnTrip
        )

        return (
          <Accordion
            key={dispatchPlace.id}
            variant={'primary'}
            className={'bg-white-hover mb-4'}
            open={accordionOpen[dispatchPlace.id] ?? false}
            onToggle={() =>
              setAccordionOpen((prev) => ({
                ...prev,
                [dispatchPlace.id]: !prev[dispatchPlace.id]
              }))
            }
            title={
              <div className={'flex items-center gap-2'}>
                <LocationOneIcon
                  color={tailwindColors.primary.dark.DEFAULT}
                  className={'w-8 h-8 bg-primary-light rounded p-1'}
                />
                <Typography variant={'sub-heading'} color={tailwindColors.primary.DEFAULT}>
                  {`Lieu de RDV ${label}`}
                </Typography>
              </div>
            }
          >
            <DispatchPlaceMeetingPlaceSection
              key={dispatchPlace.id}
              dispatchPlaceId={dispatchPlace.id}
            />
          </Accordion>
        )
      })}
    </div>
  )
}
